import md5 from "md5";

import safeProtocols from "../electron/safeProtocols";
import electron from "../electron";
import { userPrefs } from "../prefs";
import { Theme } from "../theme";

import { emotarUrl, imageUrl } from "./image";
import { getPaddingRecord } from "./padding";
import { copyContent, getScaledDimensions, highlightMatchedParts, clearNativeSelection } from "./platform_ts";
import { uppercaseFirst } from "./string";

/**
 * Finds first positioned ancestor of given element
 */
function findFirstPositionedAncestor(element) {
    if (!element) {
        return null;
    }

    let container = element.parentElement;
    let containerStyle = getComputedStyle(container);

    // Find first scrollable container
    while (container && containerStyle.position === "static") {
        container = container.parentElement;
        containerStyle = getComputedStyle(container);
    }

    return container;
}

/**
 * Returns first scrollable parent element of given element
 */
function getFirstScrollableParent(element) {
    if (!element) {
        return null;
    }

    let container = element.parentElement;

    // Find first scrollable container
    while (container && container.scrollHeight <= container.clientHeight) {
        container = container.parentElement;
    }

    return container;
}

/**
 * Checks if given element is visible in its (first scrollable) container
 * and if not scrolls to it until it snaps to the edge of its container
 */
function scrollIfNecessary(element, opts) {
    const {
        container = getFirstScrollableParent(element),
        offset = 0,
        padding = 0,
        withSetTimeout = true,
        horizontalScroll = false
    } = opts || {};

    if (!element || !container) {
        return;
    }

    const {
        top: paddingTop,
        right: paddingRight,
        bottom: paddingBottom,
        left: paddingLeft
    } = getPaddingRecord(padding);

    const scroll = () => {
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const containerTop = containerRect.top + paddingTop;
        const containerBottom = containerRect.bottom - paddingBottom;
        const containerLeft = containerRect.left + paddingLeft;
        const containerRight = containerRect.right - paddingRight;

        // Vertical scroll
        if (elementRect.top < containerTop) {
            container.scrollTop -= containerTop - elementRect.top + offset;
        } else if (elementRect.bottom > containerBottom) {
            container.scrollTop += elementRect.bottom - containerBottom + offset;
        }

        if (horizontalScroll) {
            if (elementRect.left < containerLeft) {
                container.scrollLeft -= containerLeft - elementRect.left + offset;
            } else if (elementRect.right > containerRight) {
                container.scrollLeft += elementRect.right - containerRight + offset;
            }
        }
    };

    if (withSetTimeout) {
        setTimeout(scroll, 4);
        return;
    }

    scroll();
}

/**
 * Checks if given element is visible in its (first scrollable) container
 */
function isElementVisible(element, container = getFirstScrollableParent(element)) {
    if (!container) {
        return true;
    }

    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return containerRect.top <= elementRect.top &&
           containerRect.bottom >= elementRect.bottom &&
           containerRect.left <= elementRect.left &&
           containerRect.right >= elementRect.right;
}

function isElementBelowViewport(element, container = getFirstScrollableParent(element)) {
    if (!element || !container) {
        return false;
    }

    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return elementRect.top > containerRect.bottom;
}

/**
 * Wiggles given element
 */
function wiggle(element) {
    element.addEventListener("animationend", () => {
        element.classList.remove("wiggle");
    });
    element.classList.add("wiggle");
}

/**
 * Focuses and selects all content of given input element
 */
function highlightInput(input, selectText = true) {
    setTimeout(() => {
        input.focus();
        selectText && input.select();
    }, 4);
}

/**
 * Scrolls to, focuses and selects all content of given input element
 */
function highlightAndScrollToInput(input) {
    setTimeout(() => {
        input.scrollIntoView();
        input.focus();
        input.select();
    }, 4);
}

/**
 * Checks whether given element is input element or editable content
 */
function isEditableElement(elem) {
    return ["input", "textarea"].includes(elem.tagName.toLowerCase()) || elem.isContentEditable;
}

/**
 * Checks whether copy command is supported via browser
 */
function canCopy() {
    return document.queryCommandSupported("copy");
}

/**
 * Checks whether anchor tag has `download` property
 */
function canDownload() {
    return "download" in document.createElement("a");
}

function createTextMeasurer() {
    let canvas;
    return function (text, font) {
        if (!canvas) {
            canvas = document.createElement("canvas");
        }

        const context = canvas.getContext("2d");
        context.font = font;

        return context.measureText(text).width;
    };
}

const getTextWidth = createTextMeasurer();

/**
 * Fetches the Gravatar of a user with the md5sum of user email
 * and sets the src of given img element
 */
function setAvatar(img, user, { size } = {}) {
    const avatarFrame = img.parentElement;
    avatarFrame.classList.add("loading");

    img.removeAttribute("src");
    img.removeAttribute("srcset");

    img.addEventListener("load", function stopLoading() {
        img.removeEventListener("load", stopLoading);
        avatarFrame.classList.remove("loading");
    });

    const avatar = getUserAvatar(user, {
        size: size || img.width
    });

    if (!avatar) {
        return;
    }

    Object.assign(img, avatar);
}

const EMOJIS = [
    "alien", "bear", "grapes", "meat_on_bone", "bread", "lollipop", "chicken", "cookie", "dark_sunglasses", "dart",
    "dog", "eyes", "frog", "ghost", "gift", "jack_o_lantern", "hamburger", "koala", "four_leaf_clover", "lemon",
    "lion_face", "tophat", "monkey_face", "new_moon_with_face", "hamster", "mushroom", "chestnut", "octopus", "panda_face", "penguin",
    "pig_nose", "rabbit", "mouse", "robot_face", "space_invader", "full_moon_with_face", "tennis", "tomato", "tv"
];
const userIdSums = {};
const AVATAR_SIZE = 24;

function getUserIdHashSum(userId) {
    const sum = userIdSums[userId];

    if (sum) {
        return sum;
    }

    const newSum = Array.from(md5(userId)).reduce((prev, cur) => prev + cur.charCodeAt(), 0);
    userIdSums[userId] = newSum;

    return newSum;
}

function getUserAvatar(user, { size = AVATAR_SIZE, activeTheme = Theme.Light } = {}) {
    if (!user || !user.email) {
        return {
            src: imageUrl(`/img/emotars/${activeTheme}/emotarAnonymous.png`),
            randomEmotar: imageUrl(`/img/emotars/${activeTheme}/emotarAnonymous.png`)
        };
    }

    const userIdHashSum = getUserIdHashSum(user._id);

    let randomEmotar;
    randomEmotar = EMOJIS[userIdHashSum % EMOJIS.length].replace(/_\w/g, m => m.charAt(1).toUpperCase());
    randomEmotar = imageUrl(`img/emotars/${activeTheme}/emotar${uppercaseFirst(randomEmotar)}.png`);

    if (user.emotar) {
        return {
            src: emotarUrl(user.emotar, activeTheme),
            randomEmotar
        };
    }

    if (user.avatar) {
        return {
            src: user.avatar,
            randomEmotar
        };
    }

    const gravatarURL = new URL(`https://secure.gravatar.com/avatar/${md5(user.email)}`);
    gravatarURL.searchParams.set("d", randomEmotar);

    return {
        src: `${gravatarURL}&${`s=${size}`}`,
        srcset: `${gravatarURL}&${`s=${size * 2}`} 2x`,
        randomEmotar
    };
}

function openLink(url, { inNewTab = true } = {}) {
    if (electron) {
        const urlVal = new URL(url);
        if (!safeProtocols.includes(urlVal.protocol)) {
            // only allow opening https, http, ftp and mailto links in native app
            return;
        }

        electron.actions.sendOpenExternalUrl(url);
        return;
    }

    // a.k.a. good'ol window
    let loc = window.location;
    if (inNewTab) {
        const newWindow = window.open("", "_blank");
        // [rel="noopener"]
        newWindow.opener = null;
        loc = newWindow.location;
    }

    loc.assign(url);
}

async function notify({ title, body, notifier, handler } = {}) {
    if (!window.Notification || Notification.permission !== "granted") {
        return;
    }

    const notificationStatus = await userPrefs.get("notificationStatus");
    if (!notificationStatus) {
        return;
    }

    const { src: icon } = getUserAvatar(notifier, { size: 160 });

    const notification = new Notification(title, {
        body,
        icon,
        name: 0
    });

    notification.addEventListener("show", () => {
        setTimeout(notification.close.bind(notification), 5000);
    });

    if (handler) {
        notification.addEventListener("click", handler);
    }
}

export {
    EMOJIS,
    findFirstPositionedAncestor,
    scrollIfNecessary,
    getFirstScrollableParent,
    isElementVisible,
    isElementBelowViewport,
    wiggle,
    highlightInput,
    highlightAndScrollToInput,
    highlightMatchedParts,
    isEditableElement,
    canCopy,
    canDownload,
    copyContent,
    getTextWidth,
    setAvatar,
    getUserAvatar,
    openLink,
    notify,
    getScaledDimensions,
    clearNativeSelection
};
